
import React, { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Check, ChevronsUpDown, X, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface SpecialtySelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  error?: string;
}

const SPECIALTIES = [
  "Terapia Cognitivo-Comportamental",
  "Psicanálise", 
  "Psicologia Clínica",
  "Terapia Familiar",
  "Psicologia Infantil",
  "Neuropsicologia",
  "Psicologia Organizacional",
  "Terapia de Casal",
  "Psicologia do Esporte",
  "Psicologia Escolar",
  "Psicologia Jurídica",
  "Sexologia",
  "Gerontopsicologia",
  "Psicoterapia Online",
  "Avaliação Psicológica",
  "Orientação Profissional",
  "Psicologia Positiva",
  "Coaching Psicológico",
  "Hipnoterapia",
  "EMDR",
  "Terapia do Trauma",
  "Mindfulness",
  "Ansiedade",
  "Depressão",
  "Transtornos Alimentares",
  "Dependência Química",
  "Luto e Perdas",
  "Autoestima",
  "Relacionamentos",
  "Estresse"
].filter(specialty => specialty && typeof specialty === 'string' && specialty.trim() !== "").sort();

const MAX_SPECIALTIES = 5;

const SpecialtySelect = ({ value = [], onChange, error }: SpecialtySelectProps) => {
  const [open, setOpen] = useState(false);
  const [showLimitWarning, setShowLimitWarning] = useState(false);

  // Ensure value is always a valid array
  const safeValue = React.useMemo(() => {
    if (!Array.isArray(value)) {
      return [];
    }
    return value.filter(item => item && typeof item === 'string' && item.trim() !== '');
  }, [value]);

  const handleSelect = (specialty: string) => {
    if (!specialty || specialty.trim() === "") {
      return;
    }
    
    if (safeValue.includes(specialty)) {
      // Remove specialty
      const newValue = safeValue.filter(item => item !== specialty);
      onChange(newValue);
      setShowLimitWarning(false);
    } else {
      // Add specialty
      if (safeValue.length >= MAX_SPECIALTIES) {
        setShowLimitWarning(true);
        setTimeout(() => setShowLimitWarning(false), 3000);
        return;
      }
      const newValue = [...safeValue, specialty];
      onChange(newValue);
    }
  };

  const removeSpecialty = (specialty: string) => {
    const newValue = safeValue.filter(item => item !== specialty);
    onChange(newValue);
    setShowLimitWarning(false);
  };

  const getDisplayText = () => {
    if (safeValue.length === 0) return "Selecione suas especialidades";
    if (safeValue.length === 1) return "1 especialidade selecionada";
    return `${safeValue.length} especialidades selecionadas`;
  };

  const isAtLimit = safeValue.length >= MAX_SPECIALTIES;

  return (
    <TooltipProvider>
      <div className="space-y-3">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className={cn(
                "w-full justify-between text-left",
                safeValue.length === 0 && "text-muted-foreground",
                error && "border-destructive"
              )}
            >
              {getDisplayText()}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full p-0" side="bottom" align="start">
            <Command>
              <CommandInput placeholder="Buscar especialidade..." />
              <CommandList>
                <CommandEmpty>Nenhuma especialidade encontrada.</CommandEmpty>
                <CommandGroup>
                  {SPECIALTIES.filter(specialty => specialty).map((specialty) => {
                    const isSelected = safeValue.includes(specialty);
                    const isDisabled = !isSelected && isAtLimit;
                    
                    return (
                      <CommandItem
                        key={specialty}
                        value={specialty}
                        onSelect={() => handleSelect(specialty)}
                        disabled={isDisabled}
                        className={cn(
                          "cursor-pointer",
                          isDisabled && "opacity-50 cursor-not-allowed",
                          isSelected && "bg-accent"
                        )}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            isSelected ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {specialty}
                        {isDisabled && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <AlertCircle className="ml-auto h-4 w-4 text-muted-foreground" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Máximo de {MAX_SPECIALTIES} especialidades permitidas</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Warning when trying to select more than allowed */}
        {showLimitWarning && (
          <Alert variant="destructive" className="animate-in fade-in-0 duration-300">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Você pode selecionar no máximo {MAX_SPECIALTIES} especialidades.
            </AlertDescription>
          </Alert>
        )}

        {/* Error message */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Selected specialties as removable badges */}
        {safeValue.length > 0 && (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              {safeValue.map(specialty => (
                <Badge 
                  key={specialty} 
                  variant="secondary" 
                  className="flex items-center gap-1 px-3 py-1"
                >
                  {specialty}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto p-0 ml-1 hover:bg-transparent"
                    onClick={() => removeSpecialty(specialty)}
                  >
                    <X className="h-3 w-3 cursor-pointer hover:text-destructive" />
                  </Button>
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Helper text */}
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            Selecione de 1 a {MAX_SPECIALTIES} especialidades que melhor representam sua atuação.
          </p>
          <span className={cn(
            "text-xs font-medium",
            safeValue.length === 0 && "text-muted-foreground",
            safeValue.length > 0 && safeValue.length < MAX_SPECIALTIES && "text-green-600",
            safeValue.length === MAX_SPECIALTIES && "text-amber-600"
          )}>
            {safeValue.length}/{MAX_SPECIALTIES}
          </span>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default SpecialtySelect;
